import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus, Search, ChevronDown, ChevronRight, Ship, CheckCircle2, Trash2,
  PackageSearch, Loader2, X, Edit3, Settings2, Download, CreditCard,
} from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { toast } from 'sonner';
import { AccountingLayout } from '../components/AccountingLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  listPOVendors, createPOVendor, updatePOVendor,
  listPOMethods, createPOMethod, updatePOMethod, deletePOMethod,
  listPOImports, createPOImport, updatePOImport, startPOShipment, completePOImport, deletePOImport,
} from '../lib/api';

/* ---------------- helpers ---------------- */
const fmtRp = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return 'Rp 0,00';
  return 'Rp ' + Number(n).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtRp0 = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return 'Rp 0';
  return 'Rp ' + Math.round(Number(n)).toLocaleString('id-ID');
};
const fmtNum = (n, d = 2) => {
  if (n === null || n === undefined || isNaN(Number(n))) return '0,00';
  return Number(n).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: d });
};
// Parser format Indonesia: titik=ribuan, koma=desimal. Tanpa koma, titik dianggap
// ribuan hanya jika pola grup 3 digit (1.250); selain itu dianggap desimal (2.5 → 2.5).
const parseNum = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return isFinite(v) ? v : 0;
  let s = String(v).trim().replace(/[^\d.,-]/g, '');
  if (!s) return 0;
  const neg = s.startsWith('-');
  s = s.replace(/-/g, '');
  if (s.includes(',')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (s.includes('.')) {
    const parts = s.split('.');
    const thousands = parts.length > 1 && parts[0].length >= 1 && parts[0].length <= 3
      && parts.slice(1).every((p) => p.length === 3);
    if (thousands) s = parts.join('');
  }
  const n = parseFloat(s);
  if (!isFinite(n)) return 0;
  return neg ? -n : n;
};
const fmtThousands = (v) => {
  const s = String(v).replace(/[^\d]/g, '');
  return s ? Number(s).toLocaleString('id-ID') : '';
};
// Input desimal: simpan mentah saat mengetik (jangan ganggu kursor), rapikan saat blur.
const sanitizeDecimalInput = (v) => {
  let s = String(v).replace(/[^\d,.-]/g, '');
  const ci = s.indexOf(',');
  if (ci !== -1) s = s.slice(0, ci + 1) + s.slice(ci + 1).replace(/,/g, '');
  return s;
};
const fmtDecimalInput = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = parseNum(v);
  return n.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};
const fmtKursDisplay = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return 'Rp 0,00';
  return 'Rp ' + Number(n).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const STATUS_BADGE = {
  DRAFT:       { label: 'DRAFT',       cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  ON_SHIPMENT: { label: 'ON SHIPMENT', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  DONE:        { label: 'DONE',        cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
};

const summarizeItems = (items) => {
  if (!items || items.length === 0) return '—';
  const first = items[0]?.item || '—';
  if (items.length === 1) return first;
  return `${first} · +${items.length - 1} item`;
};
const sumQty = (items) => (items || []).reduce((s, i) => s + Number(i.qty || 0), 0);

/* ---------------- page ---------------- */
export default function POImportPage() {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState('');
  const [tab, setTab] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [showVendorMgr, setShowVendorMgr] = useState(false);
  const [showMethodMgr, setShowMethodMgr] = useState(false);
  const [editingPO, setEditingPO] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPOImports();
      setOrders(Array.isArray(data) ? data : []);
    } catch { toast.error('Gagal memuat data PO Import'); }
    finally { setLoading(false); }
  }, []);
  const loadMeta = useCallback(async () => {
    try {
      const [vs, ms] = await Promise.all([listPOVendors(), listPOMethods()]);
      setVendors(Array.isArray(vs) ? vs : []);
      setMethods(Array.isArray(ms) ? ms : []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); loadMeta(); }, [load, loadMeta]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      if (tab !== 'all' && o.status !== tab) return false;
      if (vendorFilter !== 'all' && o.vendor_id !== vendorFilter) return false;
      if (dateFrom && o.po_date < dateFrom) return false;
      if (dateTo && o.po_date > dateTo) return false;
      if (!q) return true;
      const hitItem = (o.items || []).some((i) => (i.item || '').toLowerCase().includes(q));
      return (
        (o.number || '').toLowerCase().includes(q) ||
        (o.vendor_name || '').toLowerCase().includes(q) ||
        (o.payment_method || '').toLowerCase().includes(q) ||
        hitItem
      );
    });
  }, [orders, query, tab, vendorFilter, dateFrom, dateTo]);

  const filterActive = vendorFilter !== 'all' || !!dateFrom || !!dateTo || !!query.trim();
  const resetFilters = () => { setQuery(''); setVendorFilter('all'); setDateFrom(''); setDateTo(''); };

  const counts = useMemo(() => {
    const c = { all: orders.length, ON_SHIPMENT: 0, DONE: 0 };
    for (const o of orders) if (o.status in c) c[o.status] += 1;
    return c;
  }, [orders]);

  const toggleExpand = (id) => setExpanded((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const toggleSelected = (id) => setSelectedIds((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllFiltered = () => setSelectedIds((s) => {
    const allSelected = filtered.length > 0 && filtered.every((o) => s.has(o.id));
    if (allSelected) {
      const n = new Set(s); filtered.forEach((o) => n.delete(o.id)); return n;
    }
    const n = new Set(s); filtered.forEach((o) => n.add(o.id)); return n;
  });
  const clearSelection = () => setSelectedIds(new Set());
  const selectedOrders = filtered.filter((o) => selectedIds.has(o.id));
  const selectedTotal = selectedOrders.reduce((s, o) => s + Number(o.total_idr || 0), 0);
  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => selectedIds.has(o.id));

  const exportSelectedToExcel = () => {
    const rows = selectedOrders.length > 0 ? selectedOrders : filtered;
    if (rows.length === 0) { toast.error('Tidak ada PO untuk diexport'); return; }
    const rpFmt = '"Rp"#,##0.00';
    const headerStyle = {
      font: { bold: true, color: { rgb: 'FFFFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: {
        top: { style: 'thin', color: { rgb: 'FFCCCCCC' } }, bottom: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
        left: { style: 'thin', color: { rgb: 'FFCCCCCC' } }, right: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
      },
    };
    const titleStyle = {
      font: { bold: true, size: 14, color: { rgb: 'FFFFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
    const totalStyle = {
      font: { bold: true },
      fill: { patternType: 'solid', fgColor: { rgb: 'FFECFDF5' } },
      border: { top: { style: 'medium', color: { rgb: 'FF065F46' } } },
    };
    const cols = 10;
    const aoa = [];
    aoa.push([{ v: 'DAFTAR PURCHASE ORDER IMPORT', s: titleStyle }, ...Array(cols - 1).fill('')]);
    aoa.push([{ v: `Diekspor: ${new Date().toLocaleString('id-ID')} · ${rows.length} PO` }]);
    aoa.push([]);
    aoa.push([
      { v: 'No', s: headerStyle }, { v: 'Tanggal', s: headerStyle }, { v: 'No. PO', s: headerStyle },
      { v: 'Vendor', s: headerStyle }, { v: 'Item', s: headerStyle }, { v: 'Total Rp', s: headerStyle },
      { v: 'Cargo', s: headerStyle }, { v: 'Grand Total', s: headerStyle },
      { v: 'Status', s: headerStyle }, { v: 'ETA', s: headerStyle },
    ]);
    let sumRp = 0, sumCargo = 0, sumGrand = 0;
    const sorted = [...rows].sort((a, b) => (a.po_date || '').localeCompare(b.po_date || ''));
    sorted.forEach((o, i) => {
      const rp = Number(o.total_rp_before_cargo || 0);
      const cg = Number(o.total_cargo || 0);
      const gr = Number(o.total_idr || 0);
      sumRp += rp; sumCargo += cg; sumGrand += gr;
      aoa.push([
        i + 1, o.po_date || '', o.number || '', o.vendor_name || '', summarizeItems(o.items),
        { v: rp, t: 'n', z: rpFmt }, { v: cg, t: 'n', z: rpFmt }, { v: gr, t: 'n', z: rpFmt },
        (STATUS_BADGE[o.status]?.label || o.status || ''),
        o.status === 'DONE' ? '—' : (o.eta_date || '—'),
      ]);
    });
    aoa.push([
      { v: 'TOTAL', s: totalStyle }, '', '', '', '',
      { v: sumRp, t: 'n', z: rpFmt, s: totalStyle },
      { v: sumCargo, t: 'n', z: rpFmt, s: totalStyle },
      { v: sumGrand, t: 'n', z: rpFmt, s: { ...totalStyle, font: { bold: true, color: { rgb: 'FF065F46' } } } },
      '', '',
    ]);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 5 }, { wch: 12 }, { wch: 18 }, { wch: 22 }, { wch: 34 },
      { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 14 },
    ];
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: cols - 1 } }];
    ws['!rows'] = [{ hpt: 26 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'PO Import');
    const ts = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `PO_Import_${ts}.xlsx`);
    toast.success(`Excel diunduh (${rows.length} PO)`);
  };

  const doStart = async (o) => {
    setPendingId(o.id);
    try { await startPOShipment(o.id); toast.success('Shipment dimulai · ETA otomatis'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Gagal memulai shipment'); }
    finally { setPendingId(null); }
  };
  const doComplete = async (o) => {
    setPendingId(o.id);
    try { await completePOImport(o.id); toast.success('PO ditandai DONE'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Gagal menyelesaikan PO'); }
    finally { setPendingId(null); }
  };
  const doDelete = async () => {
    if (!confirmDel) return;
    try { await deletePOImport(confirmDel.id); toast.success('PO dihapus'); setConfirmDel(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Gagal menghapus PO'); }
  };

  return (
    <AccountingLayout>
      <div className="space-y-5" data-testid="po-import-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold">
              JurnalKu · Purchase Order
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-stone-900 tracking-tight mt-1">PO Import</h1>
            <p className="text-sm text-stone-500 mt-1">Input dan histori Purchase Order Import</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={exportSelectedToExcel} variant="outline"
              data-testid="po-export-selected-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export Excel
            </Button>
            <Button onClick={() => setShowMethodMgr(true)} variant="outline"
              data-testid="po-manage-methods-btn">
              <CreditCard className="w-4 h-4 mr-1.5" /> Kelola Metode
            </Button>
            <Button onClick={() => setShowVendorMgr(true)} variant="outline"
              data-testid="po-manage-vendors-btn">
              <Settings2 className="w-4 h-4 mr-1.5" /> Kelola Vendor
            </Button>
            <Button onClick={() => setShowForm(true)}
              className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="po-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Buat PO Import
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Cari No. PO, Vendor, atau Item…"
                className="h-10 pl-9 bg-white" data-testid="po-search" />
            </div>
            <div className="inline-flex rounded-lg bg-stone-100 p-1" data-testid="po-tabs">
              {[
                { k: 'all', label: 'Semua', count: counts.all },
                { k: 'ON_SHIPMENT', label: 'On Shipment', count: counts.ON_SHIPMENT },
                { k: 'DONE', label: 'Done', count: counts.DONE },
              ].map((t) => (
                <button key={t.k} onClick={() => setTab(t.k)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                    tab === t.k ? 'bg-white text-emerald-900 shadow-sm' : 'text-stone-600 hover:text-stone-900'
                  }`} data-testid={`po-tab-${t.k}`}>
                  {t.label} <span className="ml-1 text-[10px] font-mono text-stone-400">{t.count}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Vendor</Label>
              <select value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}
                className="h-9 rounded-md border border-stone-200 px-2 text-xs bg-white min-w-[160px]"
                data-testid="po-filter-vendor">
                <option value="all">Semua Vendor</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Dari</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                className="h-9 text-xs w-[140px]" data-testid="po-filter-from" />
            </div>
            <div className="flex items-center gap-1.5">
              <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Sampai</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                className="h-9 text-xs w-[140px]" data-testid="po-filter-to" />
            </div>
            {filterActive && (
              <button onClick={resetFilters}
                className="text-xs font-medium text-stone-600 hover:text-stone-900 px-2 py-1 rounded hover:bg-stone-100 transition inline-flex items-center gap-1"
                data-testid="po-filter-reset">
                <X className="w-3 h-3" /> Reset
              </button>
            )}
            <div className="text-[11px] text-stone-500 ml-auto">
              Menampilkan <span className="font-semibold text-stone-800">{filtered.length}</span> dari {orders.length} PO
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-white border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                  <th className="w-8 px-2 py-3 text-center">
                    <input type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAllFiltered}
                      className="w-4 h-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-500 cursor-pointer"
                      data-testid="po-check-all"
                      aria-label="Pilih semua"
                    />
                  </th>
                  <th className="w-8 px-2 py-3" />
                  <th className="px-3 py-3 text-left">Tanggal</th>
                  <th className="px-3 py-3 text-left">No. PO</th>
                  <th className="px-3 py-3 text-left">Vendor</th>
                  <th className="px-3 py-3 text-left">Item</th>
                  <th className="px-3 py-3 text-right">Total</th>
                  <th className="px-3 py-3 text-left">Status</th>
                  <th className="px-3 py-3 text-left">ETA</th>
                  <th className="px-3 py-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={10} className="text-center text-stone-500 py-8 text-sm">Memuat…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-12">
                    <PackageSearch className="w-10 h-10 mx-auto text-stone-300 mb-2" />
                    <div className="text-sm font-medium text-stone-600">Belum ada PO Import</div>
                    <div className="text-xs text-stone-400 mt-1">Klik "Buat PO Import" untuk mulai</div>
                  </td></tr>
                )}
                {!loading && filtered.map((o) => {
                  const isOpen = expanded.has(o.id);
                  const isChecked = selectedIds.has(o.id);
                  const st = STATUS_BADGE[o.status] || STATUS_BADGE.DRAFT;
                  return (
                    <React.Fragment key={o.id}>
                      <tr className={`border-b border-stone-100 hover:bg-stone-50 ${isChecked ? 'bg-emerald-50/40' : ''}`} data-testid={`po-row-${o.id}`}>
                        <td className="px-2 py-2.5 text-center">
                          <input type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelected(o.id)}
                            className="w-4 h-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-500 cursor-pointer"
                            data-testid={`po-check-${o.id}`}
                          />
                        </td>
                        <td className="px-2 py-2.5 text-center">
                          <button onClick={() => toggleExpand(o.id)}
                            className="p-1 rounded hover:bg-stone-200 transition"
                            data-testid={`po-expand-${o.id}`} aria-label="Detail">
                            {isOpen ? <ChevronDown className="w-4 h-4 text-stone-600" /> : <ChevronRight className="w-4 h-4 text-stone-600" />}
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-stone-700 font-mono text-xs">{o.po_date}</td>
                        <td className="px-3 py-2.5 font-mono font-semibold text-emerald-900 text-xs">{o.number}</td>
                        <td className="px-3 py-2.5 font-medium text-stone-800">{o.vendor_name}</td>
                        <td className="px-3 py-2.5 text-stone-700 max-w-[280px] truncate" title={summarizeItems(o.items)}>
                          {summarizeItems(o.items)}
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono font-semibold text-stone-900">{fmtRp(o.total_idr)}</td>
                        <td className="px-3 py-2.5">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${st.cls}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-stone-600 text-xs">
                          {o.status === 'DONE' ? '—' : (o.eta_date ? fmtDate(o.eta_date) : '—')}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => exportPOToExcel(o)}
                              className="p-1.5 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50 transition"
                              data-testid={`po-export-${o.id}`} aria-label="Export Excel" title="Download Excel">
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setEditingPO(o)}
                              className="p-1.5 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50 transition"
                              data-testid={`po-edit-${o.id}`} aria-label="Edit PO">
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            {o.status === 'DRAFT' && (
                              <button onClick={() => doStart(o)} disabled={pendingId === o.id}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 transition disabled:opacity-50"
                                data-testid={`po-start-${o.id}`}>
                                {pendingId === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Ship className="w-3 h-3" />}
                                Mulai Shipment
                              </button>
                            )}
                            {o.status === 'ON_SHIPMENT' && (
                              <button onClick={() => doComplete(o)} disabled={pendingId === o.id}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md border border-emerald-300 bg-emerald-100 text-emerald-900 hover:bg-emerald-200 transition disabled:opacity-50"
                                data-testid={`po-done-${o.id}`}>
                                {pendingId === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                                DONE
                              </button>
                            )}
                            {o.status === 'DONE' && (
                              <span className="text-[10px] text-emerald-700 font-semibold px-1">Selesai {fmtDate(o.completed_date)}</span>
                            )}
                            <button onClick={() => setConfirmDel(o)}
                              className="p-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 transition"
                              data-testid={`po-del-${o.id}`} aria-label="Hapus PO">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-stone-50/60" data-testid={`po-detail-${o.id}`}>
                          <td />
                          <td />
                          <td colSpan={8} className="px-4 py-4">
                            <PODetailPanel po={o} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Selection summary bar */}
      {selectedIds.size > 0 && (
        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="po-selection-bar">
          <div className="flex items-center gap-4 text-sm flex-wrap">
            <span className="font-semibold text-emerald-900">{selectedIds.size} PO dipilih</span>
            <span className="text-stone-700">
              Total Grand: <span className="font-mono font-semibold text-emerald-900" data-testid="po-selected-total">{fmtRp(selectedTotal)}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={exportSelectedToExcel} size="sm"
              className="bg-emerald-800 hover:bg-emerald-900 text-white h-8 text-xs"
              data-testid="po-export-selected-bar">
              <Download className="w-3.5 h-3.5 mr-1" /> Export {selectedIds.size} PO
            </Button>
            <button
              onClick={clearSelection}
              className="text-xs font-medium text-emerald-800 hover:text-emerald-900 px-2 py-1 rounded hover:bg-emerald-100 transition inline-flex items-center gap-1"
              data-testid="po-selection-clear"
            >
              <X className="w-3 h-3" /> Batal Pilih
            </button>
          </div>
        </div>
      )}

      {(showForm || editingPO) && (
        <POFormModal
          initial={editingPO}
          vendors={vendors} methods={methods}
          onVendorAdded={(v) => setVendors((s) => [...s, v].sort((a, b) => a.name.localeCompare(b.name)))}
          onMethodAdded={(m) => setMethods((s) => [...s, m].sort((a, b) => a.name.localeCompare(b.name)))}
          onClose={() => { setShowForm(false); setEditingPO(null); }}
          onSaved={() => { setShowForm(false); setEditingPO(null); load(); }} />
      )}

      {showVendorMgr && (
        <VendorManagerModal
          vendors={vendors}
          onSaved={(updated) => setVendors(updated)}
          onClose={() => setShowVendorMgr(false)} />
      )}

      {showMethodMgr && (
        <MethodManagerModal
          methods={methods}
          onChanged={loadMeta}
          onClose={() => setShowMethodMgr(false)} />
      )}

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="max-w-sm" data-testid="po-del-modal">
          <DialogHeader>
            <DialogTitle>Hapus PO {confirmDel?.number}?</DialogTitle>
            <DialogDescription>
              {confirmDel?.status === 'DRAFT'
                ? 'PO ini masih DRAFT. Aksi ini permanen dan tidak bisa dibatalkan.'
                : confirmDel?.status === 'ON_SHIPMENT'
                ? `⚠️ PO ini sedang ON SHIPMENT (ETA ${fmtDate(confirmDel?.eta_date)}). Penghapusan akan menghilangkan seluruh histori shipment. Pastikan ini benar.`
                : `⚠️ PO ini sudah DONE (selesai ${fmtDate(confirmDel?.completed_date)}). Penghapusan permanen dan menghilangkan record histori.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Batal</Button>
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="po-del-confirm">Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AccountingLayout>
  );
}

/* ---------------- detail panel ---------------- */
const shipmentDuration = (po) => {
  if (!po.shipment_date) return '—';
  const start = new Date(po.shipment_date);
  const end = po.completed_date ? new Date(po.completed_date) : null;
  if (!end) return '— (belum selesai)';
  const days = Math.round((end - start) / 86400000);
  return `${days} hari (${fmtDate(po.shipment_date)} → ${fmtDate(po.completed_date)})`;
};
const PODetailPanel = ({ po }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-xs">
      <DetailRow label="No. PO" value={<span className="font-mono">{po.number}</span>} />
      <DetailRow label="Tanggal" value={po.po_date} />
      <DetailRow label="Vendor" value={po.vendor_name} />
      <DetailRow label="Negara" value={po.country || '—'} />
      <DetailRow label="Kurs (Rp)" value={fmtKursDisplay(po.kurs)} />
      <DetailRow label="Metode" value={po.payment_method || '—'} />
      <DetailRow label="Total Qty" value={fmtNum(sumQty(po.items), 2)} />
      <DetailRow label="Total Cargo" value={fmtRp(po.total_cargo)} />
      <DetailRow label="Durasi Shipment" value={shipmentDuration(po)} />
      <DetailRow label="ETA" value={po.eta_date || '—'} />
      <DetailRow label="Transit Days" value={`${po.default_transit_days || 0} hari`} />
    </div>
    <div className="rounded-md border border-stone-200 bg-white overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-stone-100 text-stone-600">
          <tr>
            <th className="px-2 py-2 text-left">Item</th>
            <th className="px-2 py-2 text-center">Unit</th>
            <th className="px-2 py-2 text-right">Qty</th>
            <th className="px-2 py-2 text-right">Unit Price</th>
            <th className="px-2 py-2 text-right">Disc %</th>
            <th className="px-2 py-2 text-right">Fee</th>
            <th className="px-2 py-2 text-right">Total Fee</th>
            <th className="px-2 py-2 text-right">Total Valuta</th>
            <th className="px-2 py-2 text-right">Total Rp</th>
            <th className="px-2 py-2 text-right">Cargo Fee</th>
            <th className="px-2 py-2 text-right">Total Cargo</th>
            <th className="px-2 py-2 text-right bg-emerald-50 text-emerald-900">Price/Unit</th>
          </tr>
        </thead>
        <tbody>
          {(po.items || []).map((i, idx) => (
            <tr key={idx} className="border-t border-stone-100">
              <td className="px-2 py-1.5 font-medium">{i.item}</td>
              <td className="px-2 py-1.5 text-center">{i.unit}</td>
              <td className="px-2 py-1.5 text-right font-mono">{fmtNum(i.qty, 2)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{fmtNum(i.unit_price, 4)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{i.discount_pct ? `${i.discount_pct}%` : '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono">{i.fee_per_unit ? fmtNum(i.fee_per_unit, 4) : '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono">{i.total_fee_valuta ? fmtNum(i.total_fee_valuta, 4) : '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono">{fmtNum(i.total_valuta, 4)}</td>
              <td className="px-2 py-1.5 text-right font-mono">{fmtRp0(i.total_rp)}</td>
              <td className="px-2 py-1.5 text-right font-mono text-stone-500">{i.cargo_fee ? fmtRp(i.cargo_fee) : '—'}</td>
              <td className="px-2 py-1.5 text-right font-mono">{fmtRp(i.total_cargo)}</td>
              <td className="px-2 py-1.5 text-right font-mono font-bold bg-emerald-50 text-emerald-900">{fmtRp0(i.price_per_unit)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-stone-300 bg-stone-50 font-semibold">
            <td colSpan={7} className="px-2 py-2 text-right">Subtotal</td>
            <td className="px-2 py-2 text-right font-mono">{fmtNum(po.total_valuta, 4)}</td>
            <td className="px-2 py-2 text-right font-mono">{fmtRp0(po.total_rp_before_cargo)}</td>
            <td />
            <td className="px-2 py-2 text-right font-mono">{fmtRp(po.total_cargo)}</td>
            <td className="px-2 py-2 text-right font-mono text-emerald-900">{fmtRp(po.total_idr)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
);

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[9px] uppercase tracking-widest text-stone-500 font-semibold">{label}</span>
    <span className="text-stone-800">{value}</span>
  </div>
);

/* ---------------- form modal (multi-item, Excel-style) ---------------- */
const emptyItem = () => ({ item: '', unit: 'PCS', qty: '', unit_price: '', discount_pct: '', fee_per_unit: '', cargo_fee: '' });

const POFormModal = ({ initial, vendors, methods, onVendorAdded, onMethodAdded, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [poDate, setPoDate] = useState(initial?.po_date || todayISO());
  const [vendorId, setVendorId] = useState(initial?.vendor_id || '');
  const [paymentMethod, setPaymentMethod] = useState(initial?.payment_method || '');
  const [kurs, setKurs] = useState(initial?.kurs ? fmtDecimalInput(initial.kurs) : '');
  const [items, setItems] = useState(() => {
    if (initial?.items?.length) {
      return initial.items.map((i) => ({
        item: i.item || '',
        unit: i.unit || 'PCS',
        qty: i.qty ? fmtThousands(String(Math.round(i.qty))) : '',
        unit_price: i.unit_price ? fmtDecimalInput(i.unit_price) : '',
        discount_pct: i.discount_pct ? String(i.discount_pct) : '',
        fee_per_unit: i.fee_per_unit ? fmtDecimalInput(i.fee_per_unit) : '',
        cargo_fee: i.cargo_fee ? fmtDecimalInput(i.cargo_fee) : '',
      }));
    }
    return [emptyItem()];
  });
  const [saving, setSaving] = useState(false);

  const kursN = parseNum(kurs);
  const vendor = vendors.find((v) => v.id === vendorId);

  const computed = useMemo(() => {
    const rows = items.map((it) => {
      const qty = parseNum(it.qty);
      const up = parseNum(it.unit_price);
      const disc = parseNum(it.discount_pct);
      const fee = parseNum(it.fee_per_unit);
      const cargo = parseNum(it.cargo_fee);
      const totalFeeV = qty * fee;
      const totalV = qty * up * (1 - disc / 100) + totalFeeV;
      const totalRp = totalV * kursN;
      const totalCargo = qty * cargo;
      const pu = qty > 0 ? (totalRp + totalCargo) / qty : 0;
      return { qty, totalFeeV, totalV, totalRp, totalCargo, pu };
    });
    return {
      rows,
      sumV: rows.reduce((s, r) => s + r.totalV, 0),
      sumRp: rows.reduce((s, r) => s + r.totalRp, 0),
      sumCargo: rows.reduce((s, r) => s + r.totalCargo, 0),
    };
  }, [items, kursN]);

  const grandTotal = computed.sumRp + computed.sumCargo;

  const setItemField = (idx, k, v) =>
    setItems((s) => s.map((it, i) => (i === idx ? { ...it, [k]: v } : it)));
  const addRow = () => setItems((s) => [...s, emptyItem()]);
  const removeRow = (idx) => setItems((s) => s.length === 1 ? s : s.filter((_, i) => i !== idx));

  const canSave = poDate && vendorId && paymentMethod && kursN > 0 &&
    items.every((it) => it.item.trim() && parseNum(it.qty) > 0 && parseNum(it.unit_price) > 0);

  const handleVendorChange = async (val) => {
    if (val === '__add__') {
      const name = window.prompt('Nama vendor baru:');
      if (!name) return;
      const daysRaw = window.prompt('Default transit days (angka hari):', '30');
      const days = Math.max(0, parseInt(daysRaw || '30', 10) || 30);
      const country = window.prompt('Negara (opsional):', '') || '';
      try {
        const v = await createPOVendor({ name: name.trim(), country: country.trim(), default_transit_days: days });
        onVendorAdded(v); setVendorId(v.id); toast.success(`Vendor "${v.name}" ditambahkan`);
      } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal tambah vendor'); }
      return;
    }
    setVendorId(val);
  };
  const handleMethodChange = async (val) => {
    if (val === '__add__') {
      const name = window.prompt('Nama metode pembayaran baru:');
      if (!name) return;
      try {
        const m = await createPOMethod({ name: name.trim() });
        onMethodAdded(m); setPaymentMethod(m.name); toast.success(`Metode "${m.name}" ditambahkan`);
      } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal tambah metode'); }
      return;
    }
    setPaymentMethod(val);
  };

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    const payload = {
      po_date: poDate, vendor_id: vendorId, payment_method: paymentMethod, kurs: kursN,
      items: items.map((it) => ({
        item: it.item.trim(), unit: (it.unit || 'PCS').trim().toUpperCase(),
        qty: parseNum(it.qty), unit_price: parseNum(it.unit_price),
        discount_pct: parseNum(it.discount_pct),
        fee_per_unit: parseNum(it.fee_per_unit),
        cargo_fee: parseNum(it.cargo_fee),
      })),
    };
    try {
      if (isEdit) {
        await updatePOImport(initial.id, payload);
        toast.success('PO Import diperbarui');
      } else {
        await createPOImport(payload);
        toast.success('PO Import tersimpan');
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal menyimpan PO'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[92vw] xl:max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="po-form-modal">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit PO ${initial.number}` : 'Buat PO Import'}</DialogTitle>
          <DialogDescription>
            Input item seperti Excel. Sistem hitung Total Rp, Total Cargo, dan Price/Unit tiap item otomatis.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs font-semibold text-stone-600 mb-1 block">Tanggal PO</Label>
            <Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} className="h-10" data-testid="po-form-date" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-stone-600 mb-1 block">Vendor</Label>
            <select value={vendorId} onChange={(e) => handleVendorChange(e.target.value)}
              className="w-full h-10 rounded-md border border-stone-200 px-3 text-sm bg-white uppercase"
              data-testid="po-form-vendor">
              <option value="" className="normal-case">— Pilih Vendor —</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              <option value="__add__" className="text-emerald-700 font-semibold normal-case">+ Tambah Vendor Baru</option>
            </select>
            {vendor && (
              <div className="text-[10px] text-stone-500 mt-1">
                {vendor.country ? `${vendor.country} · ` : ''}Transit {vendor.default_transit_days || 0} hari
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold text-stone-600 mb-1 block">Metode Pembayaran</Label>
            <select value={paymentMethod} onChange={(e) => handleMethodChange(e.target.value)}
              className="w-full h-10 rounded-md border border-stone-200 px-3 text-sm bg-white uppercase"
              data-testid="po-form-method">
              <option value="" className="normal-case">— Pilih Metode —</option>
              {methods.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
              <option value="__add__" className="text-emerald-700 font-semibold normal-case">+ Tambah Metode Baru</option>
            </select>
          </div>
        </div>

        <div className="mt-3">
          <Label className="text-xs font-semibold text-stone-600 mb-1 block">Kurs (Rp per 1 valuta)</Label>
          <Input value={kurs} onChange={(e) => setKurs(sanitizeDecimalInput(e.target.value))}
            onBlur={(e) => setKurs(fmtDecimalInput(e.target.value))}
            inputMode="decimal" placeholder="2.700,50"
            className="h-10 max-w-xs text-right font-mono" data-testid="po-form-kurs" />
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs font-semibold text-stone-700">Daftar Item</Label>
            <Button type="button" onClick={addRow} variant="outline" size="sm" data-testid="po-add-row">
              <Plus className="w-3.5 h-3.5 mr-1" /> Tambah Baris
            </Button>
          </div>
          <div className="rounded-md border border-stone-200 overflow-x-auto">
            <table className="w-full text-xs" data-testid="po-items-table">
              <thead className="bg-stone-100 text-stone-700 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left min-w-[180px]">Item</th>
                  <th className="px-2 py-2 text-left w-[70px]">Unit</th>
                  <th className="px-2 py-2 text-right w-[80px]">Qty</th>
                  <th className="px-2 py-2 text-right w-[100px]">Unit Price</th>
                  <th className="px-2 py-2 text-right w-[70px]">Disc %</th>
                  <th className="px-2 py-2 text-right w-[80px]">Fee</th>
                  <th className="px-2 py-2 text-right w-[100px] bg-stone-200">Total Valuta</th>
                  <th className="px-2 py-2 text-right w-[120px] bg-stone-200">Total Rp</th>
                  <th className="px-2 py-2 text-right w-[100px]">Cargo Fee</th>
                  <th className="px-2 py-2 text-right w-[110px] bg-stone-200">Total Cargo</th>
                  <th className="px-2 py-2 text-right w-[130px] bg-emerald-100 text-emerald-900">Price/Unit</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const r = computed.rows[idx] || {};
                  return (
                    <tr key={idx} className="border-t border-stone-100" data-testid={`po-item-row-${idx}`}>
                      <td className="px-1 py-1">
                        <input value={it.item} onChange={(e) => setItemField(idx, 'item', e.target.value.toUpperCase())}
                          placeholder="BG 66 ULTIMAX"
                          className="w-full px-2 h-8 rounded border border-stone-200 text-xs uppercase focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          data-testid={`po-item-name-${idx}`} />
                      </td>
                      <td className="px-1 py-1">
                        <input value={it.unit} onChange={(e) => setItemField(idx, 'unit', e.target.value.toUpperCase())}
                          className="w-full px-2 h-8 rounded border border-stone-200 text-xs text-center uppercase focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={it.qty} onChange={(e) => setItemField(idx, 'qty', fmtThousands(e.target.value))}
                          inputMode="numeric" placeholder="0"
                          className="w-full px-2 h-8 rounded border border-stone-200 text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          data-testid={`po-item-qty-${idx}`} />
                      </td>
                      <td className="px-1 py-1">
                        <input value={it.unit_price} onChange={(e) => setItemField(idx, 'unit_price', sanitizeDecimalInput(e.target.value))}
                          onBlur={(e) => setItemField(idx, 'unit_price', fmtDecimalInput(e.target.value))}
                          inputMode="decimal" placeholder="0,00"
                          className="w-full px-2 h-8 rounded border border-stone-200 text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          data-testid={`po-item-up-${idx}`} />
                      </td>
                      <td className="px-1 py-1">
                        <input value={it.discount_pct} onChange={(e) => setItemField(idx, 'discount_pct', e.target.value)}
                          inputMode="decimal" placeholder="0"
                          className="w-full px-2 h-8 rounded border border-stone-200 text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={it.fee_per_unit} onChange={(e) => setItemField(idx, 'fee_per_unit', sanitizeDecimalInput(e.target.value))}
                          onBlur={(e) => setItemField(idx, 'fee_per_unit', fmtDecimalInput(e.target.value))}
                          inputMode="decimal" placeholder="0"
                          className="w-full px-2 h-8 rounded border border-stone-200 text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                      </td>
                      <td className="px-2 py-1 text-right font-mono bg-stone-50">{fmtNum(r.totalV || 0, 4)}</td>
                      <td className="px-2 py-1 text-right font-mono bg-stone-50">{fmtRp0(r.totalRp || 0)}</td>
                      <td className="px-1 py-1">
                        <input value={it.cargo_fee} onChange={(e) => setItemField(idx, 'cargo_fee', sanitizeDecimalInput(e.target.value))}
                          onBlur={(e) => setItemField(idx, 'cargo_fee', fmtDecimalInput(e.target.value))}
                          inputMode="decimal" placeholder="0,00"
                          className="w-full px-2 h-8 rounded border border-stone-200 text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          data-testid={`po-item-cargo-${idx}`} />
                      </td>
                      <td className="px-2 py-1 text-right font-mono bg-stone-50">{fmtRp(r.totalCargo || 0)}</td>
                      <td className="px-2 py-1 text-right font-mono font-bold bg-emerald-50 text-emerald-900" data-testid={`po-item-pu-${idx}`}>{fmtRp0(r.pu || 0)}</td>
                      <td className="px-1 py-1 text-center">
                        <button onClick={() => removeRow(idx)} disabled={items.length === 1}
                          className="p-1 rounded text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          data-testid={`po-item-del-${idx}`} aria-label="Hapus baris">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-stone-300 bg-stone-100 font-semibold">
                  <td colSpan={6} className="px-2 py-2 text-right">Subtotal</td>
                  <td className="px-2 py-2 text-right font-mono">{fmtNum(computed.sumV, 4)}</td>
                  <td className="px-2 py-2 text-right font-mono">{fmtRp0(computed.sumRp)}</td>
                  <td />
                  <td className="px-2 py-2 text-right font-mono">{fmtRp(computed.sumCargo)}</td>
                  <td className="px-2 py-2 text-right font-mono text-emerald-900" data-testid="po-grand-total">{fmtRp(grandTotal)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={!canSave || saving}
            className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="po-form-submit">
            {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {isEdit ? 'Simpan Perubahan' : 'Simpan PO'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- vendor manager modal ---------------- */
const VendorManagerModal = ({ vendors, onSaved, onClose }) => {
  const [rows, setRows] = useState(() =>
    vendors.map((v) => ({
      ...v,
      _days: String(v.default_transit_days ?? 0),
      _country: v.country || '',
      _dirty: false,
    }))
  );
  const [savingId, setSavingId] = useState(null);

  const setField = (id, k, v) =>
    setRows((s) => s.map((r) => (r.id === id ? { ...r, [k]: v, _dirty: true } : r)));

  const save = async (row) => {
    setSavingId(row.id);
    try {
      const updated = await updatePOVendor(row.id, {
        name: row.name, country: row._country,
        default_transit_days: Math.max(0, parseInt(row._days || '0', 10) || 0),
      });
      setRows((s) => s.map((r) => (r.id === row.id ? { ...r, ...updated, _days: String(updated.default_transit_days ?? 0), _country: updated.country || '', _dirty: false } : r)));
      onSaved(rows.map((r) => (r.id === row.id ? updated : r)));
      toast.success(`Vendor "${updated.name}" tersimpan`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan vendor');
    } finally { setSavingId(null); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="po-vendor-manager">
        <DialogHeader>
          <DialogTitle>Kelola Vendor</DialogTitle>
          <DialogDescription>
            Edit nama, negara, dan default transit days per vendor. Transit days digunakan untuk hitung ETA saat "Mulai Shipment".
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-stone-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
              <tr>
                <th className="px-3 py-2 text-left">Nama Vendor</th>
                <th className="px-3 py-2 text-left w-[130px]">Negara</th>
                <th className="px-3 py-2 text-right w-[110px]">Transit (hari)</th>
                <th className="px-3 py-2 text-right w-[90px]">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={4} className="text-center text-stone-400 py-6 text-xs">Belum ada vendor</td></tr>
              )}
              {rows.map((v) => (
                <tr key={v.id} className="border-t border-stone-100" data-testid={`vendor-row-${v.id}`}>
                  <td className="px-2 py-1.5">
                    <input value={v.name}
                      onChange={(e) => setField(v.id, 'name', e.target.value.toUpperCase())}
                      className="w-full px-2 h-8 rounded border border-stone-200 text-xs uppercase focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      data-testid={`vendor-name-${v.id}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <input value={v._country}
                      onChange={(e) => setField(v.id, '_country', e.target.value)}
                      placeholder="Opsional"
                      className="w-full px-2 h-8 rounded border border-stone-200 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500" />
                  </td>
                  <td className="px-2 py-1.5">
                    <input value={v._days}
                      onChange={(e) => setField(v.id, '_days', e.target.value.replace(/[^\d]/g, ''))}
                      inputMode="numeric"
                      className="w-full px-2 h-8 rounded border border-stone-200 text-xs text-right font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      data-testid={`vendor-days-${v.id}`} />
                  </td>
                  <td className="px-2 py-1.5 text-right">
                    <Button size="sm" onClick={() => save(v)} disabled={!v._dirty || savingId === v.id}
                      className="h-7 text-[11px] bg-emerald-800 hover:bg-emerald-900 text-white disabled:opacity-40"
                      data-testid={`vendor-save-${v.id}`}>
                      {savingId === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Simpan'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Tutup</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ---------------- payment method manager modal ---------------- */
const MethodManagerModal = ({ methods, onChanged, onClose }) => {
  const [rows, setRows] = useState(() => methods.map((m) => ({ ...m, _name: m.name, _dirty: false })));
  const [savingId, setSavingId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const setField = (id, v) =>
    setRows((s) => s.map((r) => (r.id === id ? { ...r, _name: v.toUpperCase(), _dirty: true } : r)));

  const save = async (row) => {
    setSavingId(row.id);
    try {
      const updated = await updatePOMethod(row.id, { name: row._name });
      setRows((s) => s.map((r) => (r.id === row.id ? { ...updated, _name: updated.name, _dirty: false } : r)));
      onChanged();
      toast.success(`"${updated.name}" tersimpan`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal menyimpan metode'); }
    finally { setSavingId(null); }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await deletePOMethod(confirmDel.id);
      setRows((s) => s.filter((r) => r.id !== confirmDel.id));
      onChanged();
      toast.success(`"${confirmDel.name}" dihapus`);
      setConfirmDel(null);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal menghapus'); }
  };

  return (
    <>
      <Dialog open onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="po-method-manager">
          <DialogHeader>
            <DialogTitle>Kelola Metode Pembayaran</DialogTitle>
            <DialogDescription>
              Rename atau hapus metode pembayaran. PO yang sudah tersimpan tetap menampilkan nama historis.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-stone-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
                <tr>
                  <th className="px-3 py-2 text-left">Nama Metode</th>
                  <th className="px-3 py-2 text-right w-[150px]">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={2} className="text-center text-stone-400 py-6 text-xs">Belum ada metode</td></tr>
                )}
                {rows.map((m) => (
                  <tr key={m.id} className="border-t border-stone-100" data-testid={`method-row-${m.id}`}>
                    <td className="px-2 py-1.5">
                      <input value={m._name}
                        onChange={(e) => setField(m.id, e.target.value)}
                        className="w-full px-2 h-8 rounded border border-stone-200 text-xs uppercase focus:outline-none focus:ring-1 focus:ring-emerald-500"
                        data-testid={`method-name-${m.id}`} />
                    </td>
                    <td className="px-2 py-1.5 text-right space-x-1">
                      <Button size="sm" onClick={() => save(m)} disabled={!m._dirty || savingId === m.id}
                        className="h-7 text-[11px] bg-emerald-800 hover:bg-emerald-900 text-white disabled:opacity-40"
                        data-testid={`method-save-${m.id}`}>
                        {savingId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Simpan'}
                      </Button>
                      <button onClick={() => setConfirmDel(m)}
                        className="inline-flex items-center p-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50 transition"
                        data-testid={`method-del-${m.id}`} aria-label="Hapus">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="max-w-sm" data-testid="method-del-modal">
          <DialogHeader>
            <DialogTitle>Hapus "{confirmDel?.name}"?</DialogTitle>
            <DialogDescription>
              Metode ini akan dihilangkan dari dropdown. PO yang sudah tersimpan tetap menampilkan nama historis.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Batal</Button>
            <Button onClick={remove} className="bg-red-600 hover:bg-red-700 text-white" data-testid="method-del-confirm">Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/* ---------------- Excel export ---------------- */
function exportPOToExcel(po) {
  const rpFmt = '"Rp"#,##0.00';
  const kursFmt = '"Rp"#,##0.00';
  const numFmt = '#,##0.00##';
  const pctFmt = '0.0"%"';
  const headerStyle = {
    font: { bold: true, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FF0F172A' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
      bottom: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
      left: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
      right: { style: 'thin', color: { rgb: 'FFCCCCCC' } },
    },
  };
  const titleStyle = {
    font: { bold: true, size: 16, color: { rgb: 'FFFFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FF065F46' } },
    alignment: { horizontal: 'center', vertical: 'center' },
  };
  const labelStyle = { font: { bold: true }, fill: { patternType: 'solid', fgColor: { rgb: 'FFF1F5F9' } } };
  const subtotalStyle = {
    font: { bold: true },
    fill: { patternType: 'solid', fgColor: { rgb: 'FFECFDF5' } },
    border: { top: { style: 'medium', color: { rgb: 'FF065F46' } } },
  };
  const priceUnitStyle = {
    font: { bold: true, color: { rgb: 'FF065F46' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FFECFDF5' } },
    numFmt: rpFmt,
  };

  const cols = 13; // Item, Unit, Qty, UnitPrice, Disc%, Fee, TotalFee, TotalValuta, Kurs, TotalRp, CargoFee, TotalCargo, PriceUnit
  const aoa = [];
  aoa.push([{ v: 'PURCHASE ORDER', s: titleStyle }, ...Array(cols - 1).fill('')]);
  aoa.push([]);
  aoa.push([{ v: 'No. PO', s: labelStyle }, po.number, '', { v: 'Tanggal', s: labelStyle }, po.po_date, '', { v: 'Vendor', s: labelStyle }, po.vendor_name, '', { v: 'Negara', s: labelStyle }, po.country || '—']);
  aoa.push([{ v: 'Kurs', s: labelStyle }, { v: po.kurs || 0, t: 'n', z: kursFmt }, '', { v: 'Metode', s: labelStyle }, po.payment_method || '—', '', { v: 'Status', s: labelStyle }, po.status, '', { v: 'ETA', s: labelStyle }, po.eta_date || '—']);
  aoa.push([]);
  aoa.push([
    { v: 'Item', s: headerStyle },
    { v: 'Unit', s: headerStyle },
    { v: 'Qty', s: headerStyle },
    { v: 'Unit Price', s: headerStyle },
    { v: 'Discount', s: headerStyle },
    { v: 'Fee', s: headerStyle },
    { v: 'Total Fee', s: headerStyle },
    { v: 'Total Valuta', s: headerStyle },
    { v: 'Kurs', s: headerStyle },
    { v: 'Total Rp', s: headerStyle },
    { v: 'Cargo Fee', s: headerStyle },
    { v: 'Total Cargo', s: headerStyle },
    { v: 'Price / Unit', s: { ...headerStyle, fill: { patternType: 'solid', fgColor: { rgb: 'FF047857' } } } },
  ]);
  for (const it of (po.items || [])) {
    aoa.push([
      it.item,
      it.unit,
      { v: it.qty, t: 'n', z: '#,##0' },
      { v: it.unit_price, t: 'n', z: numFmt },
      { v: it.discount_pct || 0, t: 'n', z: pctFmt },
      { v: it.fee_per_unit || 0, t: 'n', z: numFmt },
      { v: it.total_fee_valuta || 0, t: 'n', z: numFmt },
      { v: it.total_valuta || 0, t: 'n', z: numFmt },
      { v: po.kurs || 0, t: 'n', z: kursFmt },
      { v: it.total_rp || 0, t: 'n', z: rpFmt },
      { v: it.cargo_fee || 0, t: 'n', z: rpFmt },
      { v: it.total_cargo || 0, t: 'n', z: rpFmt },
      { v: it.price_per_unit || 0, s: priceUnitStyle },
    ]);
  }
  aoa.push([
    { v: 'Subtotal', s: subtotalStyle }, '', '', '', '', '', '',
    { v: po.total_valuta || 0, t: 'n', z: numFmt, s: subtotalStyle },
    '',
    { v: po.total_rp_before_cargo || 0, t: 'n', z: rpFmt, s: subtotalStyle },
    '',
    { v: po.total_cargo || 0, t: 'n', z: rpFmt, s: subtotalStyle },
    { v: po.total_idr || 0, t: 'n', z: rpFmt, s: { ...subtotalStyle, font: { bold: true, color: { rgb: 'FF065F46' } } } },
  ]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [
    { wch: 26 }, { wch: 8 }, { wch: 8 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
    { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 12 }, { wch: 14 }, { wch: 16 },
  ];
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: cols - 1 } }];
  ws['!rows'] = [{ hpt: 28 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'PO');
  XLSX.writeFile(wb, `${po.number}_${(po.vendor_name || 'vendor').replace(/[^A-Za-z0-9]/g, '_')}.xlsx`);
  toast.success(`Excel diunduh: ${po.number}`);
}


